import React, { useState } from "react";
import {
	TextField,
	Button,
	Paper,
	Typography,
	CircularProgress,
	Box,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Divider,
	CssBaseline,
} from "@mui/material";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import axios from "../../../api/axios";
import { adminWalletData, walletTypes } from "../../../data";
import copy from "copy-to-clipboard";
import useAuth from "../../../hooks/useAuth";
import * as newAxios from "axios";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
// import useAuth from "../../../hooks/useAuth";

// Styled component for the form container
const StyledPaper = styled(Paper)(({ theme }) => ({
	padding: theme.spacing(4),
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	maxWidth: 400,
	margin: "auto",
	borderRadius: "20px",
	boxShadow: theme.shadows[5],
	transition: "transform 0.2s",
	"&:hover": {
		transform: "scale(1.03)",
	},
}));

// Validation function for amount
const validateAmount = (amount) => {
	const num = parseFloat(amount);
	return !isNaN(num) && num > 0;
};

const DepositForm = () => {
	const [amount, setAmount] = useState("");
	const [selectedWallet, setSelectedWallet] = useState("");
	const [loginError, setLoginError] = useState("");
	const [loading, setLoading] = useState(false);
	const [successMsg, setSuccessMsg] = useState("");

	const { setAuth, auth, accountMode } = useAuth();
	const axiosPrivate = useAxiosPrivate();
	// const {  auth } = useAuth();

	const REAL_ACCOUNT_DEPOSIT_ENDPOINT = `/depositfunds/${auth?.id}`;

	const DEMO_ACCOUNT_DEPOSIT_ENDPOINT = `/demo-depositfunds/${auth?.id}`;

	const activeEndPoint =
		accountMode === "real"
			? REAL_ACCOUNT_DEPOSIT_ENDPOINT
			: DEMO_ACCOUNT_DEPOSIT_ENDPOINT;

	console.log(activeEndPoint);
	console.log(accountMode);

	const handleDeposit = async () => {
		setLoginError("");
		setSuccessMsg("");
		if (!validateAmount(amount)) {
			setLoginError("Please enter a valid deposit amount greater than zero.");
			return;
		}
		if (!selectedWallet && accountMode === "real") {
			setLoginError("Please select a wallet.");
			return;
		}

		setLoading(true);
		try {
			const response = await axiosPrivate.patch(activeEndPoint, {
				id: auth?.id,
				amount,
				walletAddr: selectedWallet,
				walletType: selectedWalletType,
			});
			console.log(response);
			// Assuming response.data contains success info  ....the main
			const depositMsg =
				accountMode === "real"
					? "Deposit successful!"
					: "Demo deposit confirmed - no real transaction made.";
			setSuccessMsg(depositMsg);
		} catch (error) {
			console.log(error);
			if (newAxios.isAxiosError(error) && error.response) {
				setLoginError(error.response.data.message || error.message);
			} else {
				setLoginError("An unexpected error occurred");
			}
		} finally {
			setLoading(false);
		}
	};

	const handleCopyWalletAddress = () => {
		const walletItem = adminWalletData.find(
			(wlt) => wlt.walletAddr === selectedWallet
		);
		if (walletItem) {
			copy(walletItem.walletAddr);
		}
	};

	const selectedWalletType =
		adminWalletData.find((wlt) => wlt.walletAddr === selectedWallet)
			?.walletType || "";

	console.log(selectedWalletType);
	// const closeModal = () => {
	// };

	const gotoHome = () => {
		setAuth((prev) => ({ ...prev, homeRoute: true }));
	};

	// console.log({ depmodal: auth.depModal });

	return (
		<motion.div
			initial={{ opacity: 0, y: -30 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5 }}
			className="w-[90%] min-h-[10rem] mt-[8rem]  pb-[1rem]  mb-[2rem]  mx-auto">
			<Button
				onClick={() => {
					gotoHome();
				}}
				sx={{ color: "red", mr: "auto", textDecoration: "underline" }}>
				Back to home{" "}
				<ArrowBackIosIcon sx={{ ml: "0.5rem", fontSize: "1rem" }} />
			</Button>
			<StyledPaper elevation={3}>
				{/* <Divider /> */}
				<Typography variant="h6" sx={{ fontWeight: "bold" }} gutterBottom>
					{accountMode === "demo"
						? "make a demo deposit to enjoy your demo trading feature "
						: "Deposit to start your earning journey."}
				</Typography>
				<Box
					sx={{
						pl: "1rem",
						mb: 2,
						display: accountMode === "demo" ? "none" : "block",
					}}>
					<ul
						style={{
							listStyleType: "decimal",
							color: "gray",
							fontSize: "0.9rem",
						}}>
						<li>Enter the amount you wish to deposit</li>
						<li>Choose a crypto wallet from the provided wallets</li>
						<li>
							Copy the wallet address and transfer the USD equivalent of the
							crypto
						</li>
						<li>Click on the "I have deposited this crypto" button</li>
					</ul>
				</Box>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						handleDeposit();
					}}
					style={{ width: "100%" }}
					noValidate>
					<TextField
						label="Amount in USD"
						variant="outlined"
						fullWidth
						margin="normal"
						required
						value={amount}
						onChange={(e) => setAmount(e.target.value)}
						type="number"
						inputProps={{ min: "0.01", step: "0.01" }}
					/>

					<FormControl
						variant="outlined"
						fullWidth
						margin="normal"
						required
						sx={{ display: accountMode === "demo" ? "none" : "flex" }}>
						<InputLabel id="wallet-label">Wallet</InputLabel>
						<Select
							labelId="wallet-label"
							value={selectedWallet}
							onChange={(e) => setSelectedWallet(e.target.value)}
							label="Wallet">
							{adminWalletData.map((wlt) => (
								<MenuItem key={wlt.walletAddr} value={wlt.walletAddr}>
									{wlt.walletType}{" "}
									<span className="text-[0.9rem]">{`( Network: ${wlt.network} )`}</span>
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<Button
						variant="outlined"
						color="success"
						fullWidth
						sx={{
							display: accountMode === "demo" ? "none" : "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: "0.5rem",
							mb: 2,
						}}
						onClick={handleCopyWalletAddress}
						disabled={!selectedWallet}>
						Copy Wallet Address
						<Typography variant="body2" color="textSecondary">
							{selectedWalletType}
						</Typography>
					</Button>

					<Button
						type="submit"
						variant="contained"
						color="primary"
						fullWidth
						disabled={loading}
						sx={{ position: "relative", mt: 2 }}>
						{loading && (
							<CircularProgress size={24} sx={{ position: "absolute" }} />
						)}
						{!loading && accountMode === "demo"
							? "Make demo deposit "
							: "I have deposited this crypto"}
					</Button>

					{loginError && (
						<Typography color="error" align="center" sx={{ mt: 2 }}>
							{loginError}
						</Typography>
					)}

					{successMsg && (
						<Typography color="green" align="center" sx={{ mt: 2 }}>
							{successMsg}
						</Typography>
					)}
				</form>
			</StyledPaper>
		</motion.div>
	);
};

const theme = createTheme({
	palette: {
		primary: { main: "#1976d2" },
		secondary: { main: "#dc004e" },
	},
});

export default function UDDeposit(props) {
	const { isDepositUiActive } = props;
	return (
		<ThemeProvider theme={theme}>
			<CssBaseline />
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					height: "100vh",
					backgroundColor: isDepositUiActive ? "transparent" : "#f5f5f5",
					// backgroundColor: "transparent",

					zIndex: 0,
				}}>
				<DepositForm />
			</div>
		</ThemeProvider>
	);
}
